export const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'gbraid',
  'wbraid',
  'rdt_cid',
] as const;

/**
 * UI-state query params that should survive cross-page redirects.
 *
 * Unlike `ATTRIBUTION_PARAM_KEYS` (analytics provenance), these encode the
 * destination UI state the user is trying to reach. The settings modal is
 * opened by `?settings=<slug>` and is the entry point for external links
 * (e.g. account-recovery emails linking to `/settings/billing`). Without
 * preserving the param, the auth-bounce chain (`/` → `/scribe/new-user` →
 * `/scribe/chat` …) strips the slug at each hop and the modal opens over an
 * empty page. See DS-513.
 *
 * **Adding a key here affects every callsite of
 * `appendAttributionParamsToRedirectUrl`** (~20 sites across
 * `pages/scribe/*`, `pages/onboarding`, `user-creation.tsx`,
 * `coding-dashboard`, `action-center`, etc.). Only add a key whose value
 * is safe to forward through *any* internal redirect — i.e. one that
 * downstream pages can either consume meaningfully or harmlessly ignore.
 * External / OAuth-callback paths are not a concern: every callsite
 * passes either a hardcoded `/scribe/*` route or a path validated by
 * `getSafeRedirectUrl` (which itself allowlists `/scribe` only), so the
 * preserved keys cannot leak to non-Heidi origins.
 */
export const UI_STATE_REDIRECT_PARAM_KEYS = ['settings'] as const;

export type AttributionParams = Partial<
  Record<(typeof ATTRIBUTION_PARAM_KEYS)[number], string>
>;

/**
 * Extracts attribution params from the current URL for use in analytics events.
 * Returns an object with only the params that are present.
 */
export function getAttributionParamsFromUrl(): AttributionParams {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const result: AttributionParams = {};

  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = urlParams.get(key);
    if (value) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Appends preserved query params from the current URL to a redirect URL.
 *
 * Carries two classes of params across `router.push` / `router.replace`:
 *
 * 1. **Attribution params** (`utm_*`, `gclid`, `fbclid`, …) — analytics
 *    provenance that must survive auth-bounce and onboarding hops.
 * 2. **UI-state params** (`settings`) — destination state the user is
 *    trying to reach. Required by the settings-modal external-link entry
 *    flow: without it, `/?settings=billing` → `/scribe/new-user` (or
 *    `/scribe` → `/scribe/sessions/<id>`) strips the slug and the modal
 *    has no anchor to reopen on. See DS-513.
 *
 * Existing params on the redirect URL are preserved and win — callers that
 * already encode an explicit destination param won't get clobbered.
 *
 * Named `appendAttributionParamsToRedirectUrl` for back-compat with the
 * ~20 callsites that pre-date the UI-state addition.
 */
export function appendAttributionParamsToRedirectUrl(redirectUrl: string): string {
  if (typeof window === 'undefined') return redirectUrl;

  const currentParams = new URLSearchParams(window.location.search);
  // Single loop over both classes of preserved keys — keeping them in one
  // pass avoids the "guard logic diverges between two near-identical
  // loops" footgun and makes adding a new class (e.g. session-state) a
  // one-line constant addition.
  const preservedKeys = [...ATTRIBUTION_PARAM_KEYS, ...UI_STATE_REDIRECT_PARAM_KEYS];
  const hasPreservableParams = preservedKeys.some((key) => currentParams.get(key));
  if (!hasPreservableParams) return redirectUrl;

  try {
    const resolvedUrl = new URL(redirectUrl, window.location.origin);

    for (const key of preservedKeys) {
      const value = currentParams.get(key);
      if (value && !resolvedUrl.searchParams.has(key)) {
        resolvedUrl.searchParams.set(key, value);
      }
    }

    // Keep relative redirects relative, absolute redirects absolute.
    if (/^https?:\/\//i.test(redirectUrl)) {
      return resolvedUrl.toString();
    }

    return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
  } catch {
    // If redirectUrl is malformed, return it unchanged.
    return redirectUrl;
  }
}
